import type { RealEstate } from '../../models/apiTypes';
import type { RealEstateStyle } from '../../data/realEstate';
import { getRealEstateStyle, getRealEstateName, getRealEstateDescription } from '../../data/realEstate';
import { ALL_BOX_HOUSE_IDS, EGYPT_BOX_CONFIG, BIKINI_BOTTOM_OLD_BOX_CONFIG } from '../primitives/randomBoxConfig';

export interface RealEstateCatalogItem {
  id: number;
  name: string;
  description: string;
  priceSoft: number | null | undefined;
  priceStars: number | null | undefined;
  effectivePearlsPrice: number | null;
  effectiveStarsPrice: number | null;
  discountPricePearls: number | null | undefined;
  discountPriceStars: number | null | undefined;
  firstHouseDiscountPrice: number | null | undefined;
  hasFirstHouseDiscount: boolean;
  hasGeneralDiscount: boolean;
  image: RealEstateStyle['image'];
  gradient: RealEstateStyle['gradient'];
  canBuyWithPearls: boolean;
  canBuyWithStars: boolean;
  maxQuantity: number | null | undefined;
  purchasedCount: number;
  isAvailable: boolean;
}

export type RealEstateLayoutEntry =
  | { kind: 'house'; item: RealEstateCatalogItem }
  | { kind: 'box'; config: typeof EGYPT_BOX_CONFIG };

// Функція для обчислення ефективної ціни в pearls
export const getEffectivePearlsPrice = (
  estate: RealEstate,
  firstHouseDiscountAvailable: boolean,
): number | null => {
  if (firstHouseDiscountAvailable && estate.first_house_discount_price != null) {
    return estate.first_house_discount_price;
  }
  if (estate.discount_price_pearls != null) {
    return estate.discount_price_pearls;
  }
  return estate.price_pearls ?? null;
};

// Функція для обчислення ефективної ціни в stars
export const getEffectiveStarsPrice = (estate: RealEstate): number | null => {
  return estate.discount_price_stars ?? estate.price_stars ?? null;
};

// Функція для перевірки доступності
export const isAvailable = (estate: RealEstate): boolean => {
  if (estate.max_quantity == null) return true;
  return (estate.purchased_count ?? 0) < estate.max_quantity;
};

export function buildRealEstateCatalog(
  realEstates: RealEstate[],
  firstHouseDiscountAvailable: boolean,
  t: (key: string) => string,
): RealEstateCatalogItem[] {
  // Фільтруємо всі будинки, що входять в будь-який бокс, з загального каталогу
  const filteredEstates = realEstates.filter((estate: RealEstate) =>
    !ALL_BOX_HOUSE_IDS.includes(estate.id)
  );

  return filteredEstates.map((estate: RealEstate) => {
    const style = getRealEstateStyle(estate.id);
    const effectivePearlsPrice = getEffectivePearlsPrice(estate, firstHouseDiscountAvailable);
    const effectiveStarsPrice = getEffectiveStarsPrice(estate);
    const hasFirstHouseDiscount = firstHouseDiscountAvailable && estate.first_house_discount_price != null;
    const hasGeneralDiscount = estate.discount_price_pearls != null || estate.discount_price_stars != null;

    return {
      id: estate.id,
      name: getRealEstateName(estate.id, t),
      description: getRealEstateDescription(estate.id, t),
      priceSoft: estate.price_pearls,
      priceStars: estate.price_stars,
      effectivePearlsPrice,
      effectiveStarsPrice,
      discountPricePearls: estate.discount_price_pearls,
      discountPriceStars: estate.discount_price_stars,
      firstHouseDiscountPrice: estate.first_house_discount_price,
      hasFirstHouseDiscount,
      hasGeneralDiscount,
      image: style.image,
      gradient: style.gradient,
      canBuyWithPearls: effectivePearlsPrice !== null,
      canBuyWithStars: effectiveStarsPrice !== null,
      maxQuantity: estate.max_quantity,
      purchasedCount: estate.purchased_count ?? 0,
      isAvailable: isAvailable(estate),
    };
  });
}

// Onboarding: only houses the player can buy right now with pearls, cheapest
// first — no boxes, no Stars-only or sold-out houses competing for attention.
export function buildOnboardingLayoutEntries(
  catalog: RealEstateCatalogItem[],
): RealEstateLayoutEntry[] {
  return catalog
    .filter(i => i.isAvailable && i.canBuyWithPearls)
    .sort((a, b) => (a.effectivePearlsPrice ?? 0) - (b.effectivePearlsPrice ?? 0))
    .map(item => ({ kind: 'house' as const, item }));
}

// Будує інтерлів'ований список: боксу чергуються між будинками pearls/donate
export function buildRealEstateLayoutEntries(
  catalog: RealEstateCatalogItem[],
): RealEstateLayoutEntry[] {
  type CatalogItem = RealEstateCatalogItem;
  type LayoutEntry = RealEstateLayoutEntry;

  const available = catalog.filter(i => i.isAvailable);
  const unavailable = catalog.filter(i => !i.isAvailable);

  // Розділяємо доступні на pearls і donate (stars-only), сортуємо за ціною
  const pearlItems = available
    .filter(i => i.canBuyWithPearls)
    .sort((a, b) => (a.effectivePearlsPrice ?? 0) - (b.effectivePearlsPrice ?? 0));
  const donateItems = available
    .filter(i => !i.canBuyWithPearls)
    .sort((a, b) => (a.effectiveStarsPrice ?? 0) - (b.effectiveStarsPrice ?? 0));

  // Ділимо обидва списки навпіл для двох секцій між боксами
  const pMid = Math.ceil(pearlItems.length / 2);
  const firstHalfPearls = pearlItems.slice(0, pMid);
  const secondHalfPearls = pearlItems.slice(pMid);

  const dMid = Math.ceil(donateItems.length / 2);
  const firstHalfDonate = donateItems.slice(0, dMid);
  const secondHalfDonate = donateItems.slice(dMid);

  // Перемежовуємо: Pearl, Donate, Pearl, Donate, ...
  const interleave = (pearls: CatalogItem[], donate: CatalogItem[]): LayoutEntry[] => {
    const result: LayoutEntry[] = [];
    let p = 0, d = 0;
    while (p < pearls.length || d < donate.length) {
      if (p < pearls.length) result.push({ kind: 'house' as const, item: pearls[p++] });
      if (d < donate.length) result.push({ kind: 'house' as const, item: donate[d++] });
    }
    return result;
  };

  return [
    // Зверху: Egypt бокс
    { kind: 'box' as const, config: EGYPT_BOX_CONFIG },
    // Перша половина будинків (pearls + donate перемежовано)
    ...interleave(firstHalfPearls, firstHalfDonate),
    // По центру: старий Bikini Bottom бокс
    { kind: 'box' as const, config: BIKINI_BOTTOM_OLD_BOX_CONFIG },
    // Друга половина будинків
    ...interleave(secondHalfPearls, secondHalfDonate),
    // Розпродані будинки в кінці
    ...unavailable.map(item => ({ kind: 'house' as const, item })),
  ];
}
